import { Box, Grid, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useReducer } from 'react';
import { useFetch } from 'use-http';
import { HeaderPage } from 'components';
import { MessageContainer } from 'components/Graphics/Chart.styles';
import { ColumnsChart } from 'components/Graphics/ColumnsChart';
import { HorizontalBarChart } from 'components/Graphics/HorizontalBarChart';
import { MapChart } from 'components/Graphics/MapChart';
import { apiAnalytics } from 'constants/api';
import { colors, CondizioniTransitabilitaL3Map } from 'constants/colors';
import { useGlobalSelector } from 'stores';
import { GraphicContainer } from './Analytics.styles';

const cdaConfig = {
  lowCdA: {
    label: 'Cda Bassa',
    color: '#99c567',
  },
  lowMediumCdA: {
    label: 'CdA Medio-Bassa',
    color: '#c7deb5',
  },
  mediumCdA: {
    label: 'CdA Media',
    color: '#ffe695',
  },
  mediumHighCdA: {
    label: 'CdA Medio-Alta',
    color: '#fdcb37',
  },
  highCdA: {
    label: 'CdA Alta',
    color: '#f0878a',
  },
  noData: {
    label: 'Dati insufficienti',
    color: '#bfbfbf',
  },
};

// Task 27 — stesse chiavi/colori di cdaConfig ma label più corte, come nel
// mockup cliente per "CdA idraulica L3" (legenda: Bassa/Medio-Bassa/Media/
// Medio-Alta/Alta/ND, non "Cda Bassa"/"Dati insufficienti" del grafico
// "Classi di attenzione").
const cdaL3Config = {
  lowCdA: { label: 'Bassa', color: '#99c567' },
  lowMediumCdA: { label: 'Medio-Bassa', color: '#c7deb5' },
  mediumCdA: { label: 'Media', color: '#ffe695' },
  mediumHighCdA: { label: 'Medio-Alta', color: '#fdcb37' },
  highCdA: { label: 'Alta', color: '#f0878a' },
  noData: { label: 'ND', color: '#bfbfbf' },
};

const riskCriticalConfig = {
  criticalELements: {
    label: 'Elementi critici',
    color: '#DBE6FF',
  },
  criticalCondition: {
    label: 'Condizioni critiche',
    color: '#DBE6FF',
  },
  structureSpecialInspection: {
    label: `Ispezioni speciali struttura`,
    color: '#DBE6FF',
  },
  landslideRisk: {
    label: 'Rischio frane',
    color: '#ffe695',
  },
  landslideRiskSpecialInspection: {
    label: 'Ispezioni speciali frane',
    color: '#ffe695',
  },
  hydraulicRisk: {
    label: 'Rischio idraulico',
    color: '#c7deb5',
  },
  hydraulicRiskSpecialInspection: {
    label: 'Ispezioni speciali idraulica',
    color: '#c7deb5',
  },
  interventionsAccessoryElements: {
    label: 'Interventi elementi accessori',
    color: '#FACCD5',
  },
  interventionsServices: {
    label: 'Interventi servizi',
    color: '#FACCD5',
  },
};

// Il BE garantisce liste "sempre complete" (anche a 0) solo dopo che lo
// schedule notturno ha girato almeno una volta — prima (item DynamoDB
// assente) il DTO di default ha questi due campi vuoti (`[]`), non le 5/4
// voci a value:0. Fallback FE con le stesse coppie id/label del codice BE
// (EsitoValutazioneL3/CondizioniTransitabilitaL3) per mostrare comunque i
// grafici completi, come da mockup cliente — sostituito non appena il BE
// inizia a restituire le liste popolate.
const HYDRAULIC_ACTIONS_FALLBACK = [
  { id: 1, label: 'Controllo periodico ordinario' },
  { id: 2, label: 'Controllo periodico con monitoraggio' },
  { id: 3, label: 'Ulteriori ispezioni e approfondimenti speciali' },
  { id: 4, label: 'Valutazione accurata di Livello 4' },
  { id: 5, label: 'Interventi di mitigazione del rischio' },
];
// Ordine del mockup cliente (non l'ordine dell'enum 1-4): Transitabile,
// Operativo, Adeguato, Non valutata — stesso ordine già nell'esempio del
// doc BE quando la lista arriva popolata.
const HYDRAULIC_TRANSITABILITY_FALLBACK = [
  { id: 3, label: 'Transitabile' },
  { id: 2, label: 'Operativo' },
  { id: 1, label: 'Adeguato' },
  { id: 4, label: 'Non valutata' },
];

const initialState = {
  cdaData: null,
  cdaError: null,
  monitorAspectsData: null,
  monitorAspectsError: null,
  risksData: null,
  risksError: null,
  regionsData: null,
  regionsError: null,
  l3IdraulicoData: null,
  l3IdraulicoError: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    default:
      return {
        ...state,
        [action.type]: action.data,
      };
  }
};

const Analytics = () => {
  const { get } = useFetch();
  // Istanza dedicata, non condivisa con `get` sopra: i 5 GET di questa pagina
  // partono in parallelo (Promise.all) sulla STESSA istanza use-http —
  // verificato che la chiamata L3 veniva sistematicamente segnata in errore
  // pur rispondendo 200 (stesso tipo di interferenza tra richieste
  // concorrenti già risolto altrove nell'app con un'istanza a parte).
  const { get: getL3Idraulico } = useFetch();

  const { showLoader, hideLoader } = useGlobalSelector();

  //State
  const [state, dispatch] = useReducer(reducer, initialState);

  const getAnalytics = async () => {
    const promises = [
      getAnalyticsCdA(),
      getAnalyticsMonitorAspects(),
      getAnalyticsRisks(),
      getAnalyticRegions(),
      getAnalyticsL3Idraulico(),
    ];
    showLoader();
    await Promise.all(promises);
    hideLoader();
  };

  const getAnalyticsCdA = async () => {
    try {
      const res = await get(`${apiAnalytics.CDA}`);
      dispatch({ type: 'cdaData', data: res });
    } catch (err) {
      dispatch({ type: 'cdaError', data: true });
      console.error('Error in put diagnosis: ', err);
    }
  };

  const getAnalyticsMonitorAspects = async () => {
    try {
      const res = await get(`${apiAnalytics.MONITORING}`);
      dispatch({ type: 'monitorAspectsData', data: res });
    } catch (err) {
      dispatch({ type: 'monitorAspectsError', data: true });
      console.error('Error in put diagnosis: ', err);
    }
  };

  const getAnalyticsRisks = async () => {
    try {
      const res = await get(`${apiAnalytics.RISK}`);
      dispatch({ type: 'risksData', data: res });
    } catch (err) {
      dispatch({ type: 'risksError', data: true });
      console.error('Error in put diagnosis: ', err);
    }
  };

  const getAnalyticRegions = async () => {
    try {
      const res = await get(`${apiAnalytics.REGION}`);
      dispatch({ type: 'regionsData', data: res });
    } catch (err) {
      dispatch({ type: 'regionsError', data: true });
      console.error('Error in put diagnosis: ', err);
    }
  };

  // Task 27 — sezione "L3 - idraulico": contatore + 3 grafici, stesso
  // endpoint. Vedi apps/api/plans/features/task-27-handoff-fe-analytics-l3.md.
  const getAnalyticsL3Idraulico = async () => {
    try {
      const res = await getL3Idraulico(`${apiAnalytics.L3_IDRAULICO}`);
      dispatch({ type: 'l3IdraulicoData', data: res });
    } catch (err) {
      dispatch({ type: 'l3IdraulicoError', data: true });
      console.error('Error in get analytics l3 idraulico: ', err);
    }
  };

  useEffect(() => {
    getAnalytics();
  }, []);

  // useMemo, non un plain const: senza, questi 3 array vengono ricreati (nuovo
  // riferimento) ad OGNI render di Analytics, anche quando a cambiare è un
  // altro dei 5 fetch in Promise.all (es. cdaData) — ColumnsChart rieseguirebbe
  // il suo useLayoutEffect ogni volta (dipende da `data`) e, disponendo solo
  // il `chart` e non il `root` nel cleanup, amCharts5 finisce per lamentarsi
  // di "multiple Roots" sullo stesso nodo DOM dopo un paio di render.
  // ColumnsChart (non clustered) ricava le serie da Object.keys(data[0]),
  // escludendo solo `label`: l'`id` di ColonnaAzioniIdraulicheL3 (609, a
  // differenza di ColonnaIstogrammaAspettiDaMonitorare che non ce l'ha)
  // diventerebbe altrimenti una serie in più — tolto qui, non serve al grafico.
  const hydraulicActionsChartData = useMemo(() => {
    if (!state.l3IdraulicoData) return null;
    const list = state.l3IdraulicoData.hydraulicActions?.length
      ? state.l3IdraulicoData.hydraulicActions
      : HYDRAULIC_ACTIONS_FALLBACK.map(a => ({ ...a, value: 0 }));
    return list.map(({ label, value }) => ({ label, value }));
  }, [state.l3IdraulicoData]);

  const hydraulicTransitabilityData = useMemo(() => {
    if (!state.l3IdraulicoData) return [];
    return state.l3IdraulicoData.hydraulicTransitability?.length
      ? state.l3IdraulicoData.hydraulicTransitability
      : HYDRAULIC_TRANSITABILITY_FALLBACK.map(t => ({ ...t, value: 0 }));
  }, [state.l3IdraulicoData]);

  // ColumnsChart crea le serie (e quindi l'ordine di barre/legenda) seguendo
  // Object.keys(data[0]), non l'ordine di `config` — qui dipende dall'ordine
  // delle chiavi nel JSON di cdaL3 così come arriva dal BE, che dopo il
  // round-trip su DynamoDB non è detto rispetti l'ordine di dichiarazione del
  // DTO (a differenza di cdaData sopra, calcolato al volo). Ricostruito
  // esplicitamente nell'ordine canonico (uguale a cdaL3Config) per blindare
  // la sequenza Bassa/Medio-Bassa/Media/Medio-Alta/Alta/ND a prescindere da
  // come arriva dal BE.
  const cdaL3ChartData = useMemo(() => {
    if (!state.l3IdraulicoData) return null;
    const {
      label,
      lowCdA,
      lowMediumCdA,
      mediumCdA,
      mediumHighCdA,
      highCdA,
      noData,
    } = state.l3IdraulicoData.cdaL3;
    return [
      {
        label,
        lowCdA,
        lowMediumCdA,
        mediumCdA,
        mediumHighCdA,
        highCdA,
        noData,
      },
    ];
  }, [state.l3IdraulicoData]);

  return (
    <>
      <HeaderPage title="Analytics" />

      <Grid container spacing={4}>
        <Grid item sm={6}>
          <GraphicContainer>
            <Typography variant={'h2'} sx={{ margin: '20px' }}>
              Ponti per regione
            </Typography>
            <MapChart
              chartID="region-chart"
              notAnimated
              dataRegion={state.regionsData}
              error={state.regionsError}
            />
          </GraphicContainer>
        </Grid>

        <Grid item sm={6}>
          <GraphicContainer>
            <Typography variant={'h2'} sx={{ margin: '20px' }}>
              Classi di attenzione
            </Typography>
            <ColumnsChart
              chartID="columns-chart-1"
              data={state.cdaData}
              error={state.cdaError}
              config={cdaConfig}
              isClustered
              hasLegend
            />
          </GraphicContainer>
        </Grid>

        <Grid item sm={6}>
          <GraphicContainer>
            <Typography variant={'h2'} sx={{ margin: '20px' }}>
              Rischi e criticità
            </Typography>
            <HorizontalBarChart
              chartID="chart"
              data={state.risksData}
              error={state.risksError}
              config={riskCriticalConfig}
              hasTooltip
            />
          </GraphicContainer>
        </Grid>

        <Grid item sm={6} sx={{ marginBottom: 5 }}>
          <GraphicContainer>
            <Typography variant={'h2'} sx={{ margin: '20px' }}>
              Aspetti da monitorare
            </Typography>
            <ColumnsChart
              chartID="monitoring-chart"
              data={state.monitorAspectsData}
              error={state.monitorAspectsError}
            />
          </GraphicContainer>
        </Grid>

        {/* Task 27 — sezione "L3 - idraulico": 3 grafici, perimetro ponti con
        l'ultima attività L3 idraulico chiusa (diverso dal contatore "L3
        idraulici / Svolti" della Dashboard, che conta qualsiasi stato).
        Contatore come badge accanto al titolo (non un 4° grafico in griglia),
        stesso layout del mockup cliente — non i riquadri uguali usati sopra
        per le altre sezioni. */}
        <Grid item xs={12}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            flexWrap="wrap"
            sx={{ margin: '20px 20px 0' }}
          >
            <Typography variant={'h1'}>L3 - idraulico</Typography>
            {state.l3IdraulicoError ? (
              <Typography variant="h3Bold">
                {`Contatore non disponibile: si è verificato un errore.`}
              </Typography>
            ) : (
              <Box
                sx={{
                  backgroundColor: colors.LIGHTGREY,
                  borderRadius: '8px',
                  padding: '8px 16px',
                }}
              >
                <Typography variant="h3Bold">
                  {state.l3IdraulicoData?.bridgesWithClosedL3 ?? '...'} ponti
                  con L3 idraulico
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>

        <Grid item sm={6}>
          <GraphicContainer>
            <Typography variant={'h2'} sx={{ margin: '20px' }}>
              CdA idraulica L3
            </Typography>
            <ColumnsChart
              chartID="columns-chart-l3-cda"
              data={cdaL3ChartData}
              error={state.l3IdraulicoError}
              config={cdaL3Config}
              isClustered
              hasLegend
            />
          </GraphicContainer>
        </Grid>

        <Grid item sm={6}>
          <GraphicContainer>
            <Typography variant={'h2'} sx={{ margin: '20px' }}>
              Azioni idrauliche
            </Typography>
            <ColumnsChart
              chartID="l3-hydraulic-actions-chart"
              data={hydraulicActionsChartData}
              error={state.l3IdraulicoError}
            />
          </GraphicContainer>
        </Grid>

        <Grid item sm={12} sx={{ marginBottom: 5 }}>
          <GraphicContainer>
            <Typography variant={'h2'} sx={{ margin: '20px' }}>
              Transitabilità idraulica
            </Typography>
            {state.l3IdraulicoError ? (
              <MessageContainer>
                <Typography variant="h3Bold">
                  {`Si è verificato un errore. Impossibile visualizzare il dato.`}
                </Typography>
              </MessageContainer>
            ) : (
              <Stack
                direction="row"
                justifyContent="center"
                spacing={3}
                flexWrap="wrap"
                sx={{ padding: '0 20px 20px' }}
              >
                {hydraulicTransitabilityData.map(item => (
                  <Stack key={item.id} alignItems="center" spacing={0.5}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor:
                          CondizioniTransitabilitaL3Map[item.id]?.labelColor,
                      }}
                    >
                      <Typography variant="h2" sx={{ color: colors.WHITE }}>
                        {item.value}
                      </Typography>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ textTransform: 'uppercase', textAlign: 'center' }}
                    >
                      {item.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </GraphicContainer>
        </Grid>
      </Grid>
    </>
  );
};

export default Analytics;
